import React, { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import tw from 'tailwind-styled-components';

const OTPModal = ({ isOpen, onClose, onVerify, email, resendOtp }) => {
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (isOpen && timeLeft > 0 && !canResend) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [isOpen, timeLeft, canResend]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setTimeLeft(300);   // 5 minutes
      setCanResend(false);
      setOtpValues(['', '', '', '', '', '']);
      setError('');
      setIsLoading(false);
      // Focus first input after modal is fully rendered
      setTimeout(() => inputRefs[0]?.current?.focus(), 100);
    }
  }, [isOpen]);

  const formatTime = (seconds) => {        // Format seconds as minutes:seconds
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    setError(''); // Clear error when user enters valid input

    // Move to next input if value is entered 
    if (value && index < inputRefs.length - 1) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Move to next input on right arrow
    else if (e.key === 'ArrowRight' && index < inputRefs.length - 1) {
      inputRefs[index + 1].current?.focus();
    }
    // Move to previous input on left arrow
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {      // handle paste event to fill OTP inputs from clipboard
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6); // Only take first 6 digits

    const newOtpValues = [...otpValues];
    for (let i = 0; i < digits.length && i < 6; i++) {
      newOtpValues[i] = digits[i];
    }
    setOtpValues(newOtpValues);          // Update state with pasted digits
    setError('');


    // Focus the next empty input or the last filled input
    const nextIndex = Math.min(digits.length, 3);
    inputRefs[nextIndex]?.current?.focus();
  };

  const handleVerify = async () => {      //verify if the otp is complete or not
    const otp = otpValues.join('');

    if (otp.length === 0) {
      setError('Please enter the OTP sent to your email');
      inputRefs[0]?.current?.focus();
      return;
    }

    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    // Check if OTP contains only numbers
    if (!/^\d{6}$/.test(otp)) {
      setError('OTP should contain only numbers');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onVerify(otp);

    } catch (error) {
      console.error('OTP verification failed:', error);
      setError(error.message || 'Invalid OTP. Please try again.');
      // Clear OTP inputs on error
      setOtpValues(['', '', '', '', '', '']);
      inputRefs[0]?.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {          // Resend OTP logic
    if (!canResend || isLoading) return;

    setIsLoading(true);
    setError('');

    try {
      if (resendOtp) {
        await resendOtp();
      } else {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setTimeLeft(300); // Reset timer to 5 minutes
      setCanResend(false);              // Reset timer and resend state
      setOtpValues(['', '', '', '', '', '']);
      inputRefs[0]?.current?.focus();           // Focus first input after resend
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isOtpComplete = otpValues.every(value => value !== '');     // Check if all OTP inputs are filled or not

  return (
    <ModalOverlay>
      <ModalContainer>

        <CloseButton
          onClick={onClose}
          disabled={isLoading}
        >
          <FaTimes className="w-4 h-4" />
        </CloseButton>

        <HeaderSection>
          <Title>
            OTP Verification
          </Title>
          <SubTitle>
            Enter the verification code sent to {email}
          </SubTitle>
        </HeaderSection>

        {/* Error Message */}
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        {/* OTP Input Grid */}
        <OtpInputContainer>
          {otpValues.map((value, index) => (
            <OtpInputWrapper key={index}>
              <OtpInput
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"            // Numeric keypad on mobile
                autoComplete="one-time-code"    // Enables SMS code auto-fill
                pattern="\d{6}"            // Pattern for 6 digits
                maxLength={1}
                value={value}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                $hasValue={!!value}
                $isLoading={isLoading}
                aria-label={`Digit ${index + 1}`}
              />
            </OtpInputWrapper>
          ))}
        </OtpInputContainer>

        {/* Timer */}
        <TimerSection>
          <TimerDisplay>
            {formatTime(timeLeft)}
          </TimerDisplay>
          <TimerText>
            Not received OTP?{' '}
            <ResendButton
              onClick={handleResend}
              disabled={!canResend || isLoading}
              $canResend={canResend && !isLoading}
              $isLoading={isLoading}
            >
              {isLoading ? 'Sending...' : 'Resend Now'}
            </ResendButton>
          </TimerText>
        </TimerSection>

        {/* Verify Button */}
        <VerifyButton
          onClick={handleVerify}
          disabled={!isOtpComplete || isLoading}
          $isComplete={isOtpComplete && !isLoading}
          $isLoading={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify OTP'}
        </VerifyButton>

      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-md
`;

const ModalContainer = tw.div`
  bg-white rounded-xl max-w-md w-full p-6 relative shadow-2xl
`;

const CloseButton = tw.button`
  absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors
`;

const HeaderSection = tw.div`
  mb-8
`;

const Title = tw.h2`
  text-2xl font-semibold text-gray-800 mb-2
`;

const SubTitle = tw.p`
  text-gray-500 text-sm
`;

const ErrorMessage = tw.div`
  bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm
`;

const OtpInputContainer = tw.div`
  grid grid-cols-6 gap-2 sm:gap-3 mb-8 md:px-10 px-3 
`;

const OtpInputWrapper = tw.div`
  relative
`;

const OtpInput = tw.input`
  w-full h-14 text-center text-2xl font-semibold border-2 rounded-lg transition-all duration-200 focus:outline-none
  ${props => props.$hasValue
    ? 'border-kuality-blue text-gray-500'
    : 'border-gray-300 focus:border-kuality-blue'
  }
  ${props => props.$isLoading ? 'opacity-50 cursor-not-allowed' : ''}
`;

const TimerSection = tw.div`
  text-center mb-6
`;

const TimerDisplay = tw.div`
  text-3xl font-mono text-gray-800 mb-2
`;

const TimerText = tw.div`
  text-gray-500 text-sm
`;

const ResendButton = tw.button`
  font-medium transition-colors
  ${props => props.$canResend
    ? 'text-kuality-blue hover:text-kuality-dark cursor-pointer'
    : 'text-gray-300 cursor-not-allowed'
  }
`;

const VerifyButton = tw.button`
  w-full py-3 rounded-lg font-medium transition-all duration-200
  ${props => props.$isComplete
    ? 'bg-kuality-blue hover:bg-kuality-dark text-white shadow-lg hover:shadow-xl'
    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
  }
`;

export default OTPModal;